import React from 'react';
import { Search, BookOpen, MessageCircle, Video, FileText, Mail, Clock, Users, Zap, Target, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const HelpCenter: React.FC = () => {
  const faqCategories = [
    {
      title: "Getting Started",
      icon: Zap,
      faqs: [
        {
          question: "How do I create my first 30-day sprint?",
          answer: "After signing up, you'll be guided through an onboarding process where you'll define your business idea, set your development stage, and create your personalized sprint plan. Our AI will then generate a week-by-week breakdown of tasks."
        },
        {
          question: "What if I don't have a business idea yet?",
          answer: "No worries! We have idea generation tools and templates to help you brainstorm. You can also browse our success stories for inspiration or use our AI validation to test different concepts."
        },
        {
          question: "How much time do I need to commit daily?",
          answer: "We recommend 1-2 hours per day for optimal results. However, even 30 minutes daily can make a significant difference. The key is consistency, not duration."
        }
      ]
    },
    {
      title: "AI Validation",
      icon: Target,
      faqs: [
        {
          question: "How accurate is the AI validation?",
          answer: "Our AI uses advanced algorithms trained on thousands of successful and failed business cases. It provides critical feedback based on real market data and investor perspectives, with an 85% accuracy rate."
        },
        {
          question: "Can I get multiple validations for different ideas?",
          answer: "Yes! You can validate multiple ideas during your subscription. Each validation includes detailed feedback, probing questions, and ratings across multiple dimensions."
        },
        {
          question: "What if the AI gives negative feedback?",
          answer: "Negative feedback is valuable! It helps you identify weaknesses early and pivot before investing significant time and money. Many successful entrepreneurs credit our AI with saving them from pursuing flawed concepts."
        }
      ]
    },
    {
      title: "Boss Mode & Accountability",
      icon: TrendingUp,
      faqs: [
        {
          question: "How does Cashpurr keep me accountable?",
          answer: "Cashpurr generates daily task lists from your sprint plan, sends reminders, tracks deadlines, and provides motivational (and sometimes critical) feedback. Think of it as having a tough but fair boss who wants you to succeed."
        },
        {
          question: "What happens if I miss a deadline?",
          answer: "Cashpurr will remind you and help you get back on track. Missing deadlines is normal in entrepreneurship - the key is learning from it and adjusting your plan accordingly."
        },
        {
          question: "Can I customize my accountability settings?",
          answer: "Absolutely! You can set your preferred reminder frequency, choose your accountability style (gentle to strict), and even set up email notifications for important milestones."
        }
      ]
    },
    {
      title: "Billing & Subscription",
      icon: BookOpen,
      faqs: [
        {
          question: "What's included in my subscription?",
          answer: "All subscriptions include AI validation, daily accountability, 30-day sprint plans, launch playbooks, ROI tracking, and community access. Yearly plans include additional features like priority support and exclusive masterclasses."
        },
        {
          question: "Can I cancel anytime?",
          answer: "Yes, you can cancel your subscription at any time. You'll continue to have access until the end of your current billing period."
        },
        {
          question: "Do you offer refunds?",
          answer: "We offer a 30-day money-back guarantee. If you're not satisfied with our platform within the first 30 days, we'll provide a full refund."
        }
      ]
    }
  ];

  const supportChannels = [
    {
      icon: MessageCircle,
      title: "Live Chat",
      description: "Get instant help from our support team",
      availability: "24/7",
      responseTime: "Usually within 5 minutes",
      action: "Start Chat"
    },
    {
      icon: Mail,
      title: "Email Support",
      description: "Send us detailed questions or issues",
      availability: "24/7",
      responseTime: "Within 4 hours",
      action: "Send Email"
    }
  ];

  const resources = [
    {
      icon: Video,
      title: "Video Tutorials",
      description: "Step-by-step guides for all features",
      count: "50+ videos"
    },
    {
      icon: FileText,
      title: "Documentation",
      description: "Comprehensive guides and FAQs",
      count: "100+ articles"
    },
    {
      icon: Users,
      title: "Community Forum",
      description: "Connect with fellow entrepreneurs",
      count: "500+ members"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-teal-500 to-orange-400 text-white pt-32 pb-40">
        <div className="absolute inset-0 bg-dot-white/10" />
        <div className="container mx-auto px-4 relative">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-6xl md:text-7xl font-light tracking-tight mb-8 leading-tight">
              Help Center
            </h1>
            
            <p className="text-xl md:text-2xl text-blue-100 mb-12 leading-relaxed max-w-2xl mx-auto font-light">
              Find answers to your questions, learn how to use our platform, and get the support you need to succeed
            </p>
            
            {/* Search Bar */}
            <div className="max-w-2xl mx-auto mb-16">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-6 h-6 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search for help articles, tutorials, or FAQs..."
                  className="w-full pl-12 pr-4 py-4 text-lg bg-white text-black border-0 rounded-lg placeholder-gray-500"
                />
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto">
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">100+</div>
                <div className="text-black-200 text-sm">Help Articles</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">50+</div>
                <div className="text-black-200 text-sm">Video Tutorials</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">24/7</div>
                <div className="text-black-200 text-sm">Support Available</div>
              </div>
            </div>
            
            {/* FAQ CTA */}
            <div className="mt-12">
              <a href="/faq">
                <Button className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-300 hover:to-orange-400 text-black font-semibold">
                  Browse Full FAQ
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Support Channels */}
      <section className="py-32 bg-white text-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-light mb-8">Get Help Fast</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto font-light">
              Multiple ways to get the support you need, when you need it
            </p>
          </div>
          
          {/* Center two cards by using 1/2 columns and container max-width */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {supportChannels.map((channel, index) => (
              <div key={index} className="text-center p-8 border border-gray-200 rounded-lg hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 border border-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                  <channel.icon className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-medium mb-4 text-black">{channel.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {channel.description}
                </p>
                <div className="space-y-2 mb-6">
                  <div className="text-sm">
                    <span className="text-gray-500">Available:</span>
                    <span className="text-black font-medium ml-2">{channel.availability}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-gray-500">Response:</span>
                    <span className="text-black font-medium ml-2">{channel.responseTime}</span>
                  </div>
                </div>
                {channel.title === 'Email Support' ? (
                  <a href="mailto:hello@startupstreak.com">
                    <Button className="w-full bg-black text-white hover:bg-gray-800">
                      {channel.action}
                    </Button>
                  </a>
                ) : (
                  <Button className="w-full bg-black text-white hover:bg-gray-800">
                    {channel.action}
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Categories */}
      <section className="py-32 bg-gray-50 text-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-light mb-8">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto font-light">
              Quick answers to the most common questions about our platform
            </p>
          </div>
          
          <div className="max-w-6xl mx-auto">
            {faqCategories.map((category, categoryIndex) => (
              <div key={categoryIndex} className="mb-16">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-12 h-12 border border-gray-200 rounded-full flex items-center justify-center">
                    <category.icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-3xl font-light text-black">{category.title}</h3>
                </div>
                
                <div className="grid lg:grid-cols-2 gap-8">
                  {category.faqs.map((faq, faqIndex) => (
                    <div key={faqIndex} className="bg-white p-8 rounded-lg border border-gray-200 hover:shadow-lg transition-shadow">
                      <h4 className="text-xl font-medium mb-4 text-black">{faq.question}</h4>
                      <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Support */}
      <section className="py-32 bg-gradient-to-br from-slate-900 via-blue-900 to-teal-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-dot-white/5" />
        <div className="container mx-auto px-4 relative">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-5xl md:text-6xl font-light mb-8">
              Still Need Help?
            </h2>
            <p className="text-xl md:text-2xl mb-12 text-blue-200 font-light">
              Our support team is here to help you succeed with your 30-day sprint
            </p>
            
            <div className="grid md:grid-cols-2 gap-8 max-w-2xl mx-auto mb-12">
              <div className="text-center">
                <div className="w-16 h-16 border border-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-medium mb-2">Response Time</h3>
                <p className="text-blue-200 text-sm">Usually within 4 hours</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 border border-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-medium mb-2">Expert Team</h3>
                <p className="text-blue-200 text-sm">Entrepreneurs helping entrepreneurs</p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <a href="mailto:hello@startupstreak.com">
                <Button size="lg" className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-300 hover:to-orange-400 text-black text-lg px-12 py-4 font-semibold shadow-2xl hover:shadow-yellow-400/25 transition-all">
                  Contact Support
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HelpCenter;
